import React, { useContext, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Select,
  MenuItem,
  FormControl,
  TextField,
  InputAdornment,
  Tooltip,
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import axios from 'axios';
import { storeContext } from '../provider/Provider';
import CustomSortIcon from '../common/CustomSortIcon';

const { REACT_APP_FLASK_API_URL } = process.env;

const StyledTooltip = withStyles(() => ({
  tooltip: {
    fontSize: 14,
    maxWidth: 500,
    padding: '8px 12px',
    lineHeight: 1.5,
  },
  arrow: {
    color: '#333',
  },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
  wrapper: {
    margin: 20,
    [theme.breakpoints.down('sm')]: {
      margin: '0 15px',
    },
  },
  heading: {
    fontWeight: 700,
    fontSize: 25,
    color: '#58595B',
    marginBottom: 16,
  },
  card: {
    padding: 20,
    background: 'white',
    borderRadius: 16,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    marginBottom: 20,
  },
  statBox: {
    padding: 16,
    background: 'white',
    borderRadius: 16,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    textAlign: 'center',
    flex: 1,
    minWidth: 140,
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    fontFamily: 'monospace',
  },
  statLabel: {
    fontSize: 11,
    color: '#999',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  filterChip: {
    margin: '0 4px 4px 0',
    fontWeight: 600,
    fontSize: 12,
  },
  rankBadge: {
    display: 'inline-block',
    fontFamily: 'monospace',
    fontWeight: 700,
    fontSize: 12,
    padding: '2px 8px',
    borderRadius: 4,
    minWidth: 32,
    textAlign: 'center',
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#58595B',
    marginBottom: 12,
  },
}));

// Intent classification
const TRANSACTIONAL_PATTERNS = [
  /\b(best|top|find|hire|recommend|rated|review)\b/i,
  /\b(lawyer|attorney|law\s*firm|legal\s+representation)\b.*\b(in|near|around)\b/i,
  /\b(near\s+me)\b/i,
  /\bwho\s+(are|is)\s+the\s+(best|top)\b/i,
];

function classifyIntent(query) {
  const match = TRANSACTIONAL_PATTERNS.some((pattern) => pattern.test(query));
  return match ? 'transactional' : 'informational';
}

function getRankColor(rank) {
  if (rank <= 3) return '#10b981';
  if (rank <= 6) return '#f59e0b';
  return '#ef4444';
}

function getRankBg(rank) {
  if (rank <= 3) return '#10b98120';
  if (rank <= 6) return '#f59e0b20';
  return '#ef444420';
}

const QueryVisibility = () => {
  const classes = useStyles();
  const { state } = useContext(storeContext);
  const domain = state.websiteSwitch.currentWebsite;
  const domainName = domain ? domain.split('.')[0] : '';

  const [gridQueries, setGridQueries] = useState(null);
  const [gridLoading, setGridLoading] = useState(false);
  const [chatGptMentions, setChatGptMentions] = useState(null);
  const [googleMentions, setGoogleMentions] = useState(null);
  const [mentionsLoading, setMentionsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [intentFilter, setIntentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [practiceAreaFilter, setPracticeAreaFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    if (!domain) return;

    // Fetch grid queries
    setGridLoading(true);
    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/queries`)
      .then((res) => {
        if (!res.data.error) {
          setGridQueries(res.data.data);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setGridLoading(false));

    // Fetch LLM mentions from both platforms
    setMentionsLoading(true);
    const mentionPayload = (platform) => ({
      domain,
      platform,
      search_scope: ['sources'],
      order_by: ['ai_search_volume,desc'],
      limit: 100,
    });

    Promise.all([
      axios
        .post(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/llm-mentions/search`, mentionPayload('chat_gpt'))
        .then((res) => { if (!res.data.error) setChatGptMentions(res.data.data); })
        .catch(() => {}),
      axios
        .post(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/llm-mentions/search`, mentionPayload('google'))
        .then((res) => { if (!res.data.error) setGoogleMentions(res.data.data); })
        .catch(() => {}),
    ]).finally(() => setMentionsLoading(false));
  }, [domain]);

  // Unify both data sources
  const unifiedQueries = useMemo(() => {
    const queries = [];

    // Grid search prompts -> always transactional
    if (gridQueries?.queries) {
      gridQueries.queries.forEach((gq, idx) => {
        queries.push({
          id: `grid-${idx}-${gq.practice_area}-${gq.city}`,
          query: gq.prompt,
          intent: 'transactional',
          source: 'grid_search',
          platform: 'ChatGPT',
          status: gq.found ? 'found' : 'missing',
          rank: gq.rank,
          visibilityPct: gq.visibility_pct,
          aiVolume: null,
          practiceArea: gq.practice_area,
          city: gq.city,
          searchedAt: gq.searched_at,
          snippet: gq.snippet || null,
          otherSources: [],
          fanOutQueries: [],
        });
      });
    }

    // LLM mentions -> classify intent, tag with platform
    const platformLabel = { chat_gpt: 'ChatGPT', google: 'Google AI Overview' };
    const mentionSources = [
      ...(chatGptMentions?.items || []).map((item) => ({ ...item, mentionPlatform: 'chat_gpt' })),
      ...(googleMentions?.items || []).map((item) => ({ ...item, mentionPlatform: 'google' })),
    ];

    const seenQuestions = new Set();
    mentionSources
      .filter((item) => item.question)
      .forEach((item) => {
        // Deduplicate by question text — keep first occurrence
        if (seenQuestions.has(item.question)) return;
        seenQuestions.add(item.question);

        const omegaSource = item.sources?.find((s) => s.domain?.includes(domainName));
        const otherSources = (item.sources || [])
          .filter((s) => !s.domain?.includes(domainName))
          .map((s) => s.domain?.replace('www.', '') || '')
          .filter(Boolean);

        const rank = omegaSource?.position || null;
        const visPct = rank ? Math.max(0, 110 - rank * 10) : 0;

        queries.push({
          id: `mention-${item.mentionPlatform}-${item.question}`,
          query: item.question,
          intent: classifyIntent(item.question),
          source: 'llm_mentions',
          platform: platformLabel[item.mentionPlatform] || item.mentionPlatform,
          status: omegaSource ? 'found' : 'missing',
          rank,
          visibilityPct: visPct,
          aiVolume: item.ai_search_volume,
          practiceArea: null,
          city: null,
          searchedAt: item.last_response_at,
          snippet: item.answer || null,
          otherSources,
          fanOutQueries: item.fan_out_queries || [],
        });
      });

    return queries;
  }, [gridQueries, chatGptMentions, googleMentions, domainName]);

  // Filter options
  const practiceAreas = useMemo(() => {
    const set = new Set();
    unifiedQueries.forEach((q) => { if (q.practiceArea) set.add(q.practiceArea); });
    return Array.from(set).sort();
  }, [unifiedQueries]);

  const locations = useMemo(() => {
    const set = new Set();
    unifiedQueries.forEach((q) => { if (q.city) set.add(q.city); });
    return Array.from(set).sort();
  }, [unifiedQueries]);

  // Apply filters
  const filtered = useMemo(() => unifiedQueries.filter((q) => {
    if (intentFilter !== 'all' && q.intent !== intentFilter) return false;
    if (statusFilter !== 'all' && q.status !== statusFilter) return false;
    if (practiceAreaFilter !== 'all' && q.practiceArea !== practiceAreaFilter) return false;
    if (locationFilter !== 'all' && q.city !== locationFilter) return false;
    return true;
  }), [unifiedQueries, intentFilter, statusFilter, practiceAreaFilter, locationFilter]);

  // Sort handler
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  // Apply search + sort
  const sortedFiltered = useMemo(() => {
    let result = filtered;

    // Text search
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter((q) => q.query.toLowerCase().includes(lower));
    }

    // Sort
    if (sortBy) {
      result = [...result].sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];

        // Handle nulls — push to bottom
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        // Numeric fields
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        }

        // String fields
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [filtered, searchText, sortBy, sortDir]);

  // Stats
  const totalFound = unifiedQueries.filter((q) => q.status === 'found').length;
  const totalMissing = unifiedQueries.filter((q) => q.status === 'missing').length;
  const avgVisibility =
    unifiedQueries.length > 0
      ? Math.round(unifiedQueries.reduce((sum, q) => sum + q.visibilityPct, 0) / unifiedQueries.length)
      : 0;
  const totalTransactional = unifiedQueries.filter((q) => q.intent === 'transactional').length;
  const totalInformational = unifiedQueries.filter((q) => q.intent === 'informational').length;

  const isLoading = gridLoading || mentionsLoading;

  if (!domain) {
    return (
      <div className={classes.wrapper}>
        <Typography className={classes.heading}>Query Visibility</Typography>
        <Paper className={classes.card}>
          <Typography>Please select a website first.</Typography>
        </Paper>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <Typography className={classes.heading}>Query Visibility</Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        All queries where {domain} appears or should appear in AI responses
      </Typography>

      {isLoading && <LinearProgress style={{ marginBottom: 16 }} />}
      {error && (
        <Paper className={classes.card}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {!isLoading && (
        <>
          {/* Stat cards */}
          <Box display="flex" flexWrap="wrap" style={{ gap: 12 }} mb={2}>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>Total Queries</div>
              <div className={classes.statValue} style={{ color: '#0d9373' }}>{unifiedQueries.length}</div>
              <div style={{ fontSize: 10, color: '#999' }}>
                {totalTransactional} transactional &middot; {totalInformational} informational
              </div>
            </div>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>Shown In</div>
              <div className={classes.statValue} style={{ color: '#10b981' }}>{totalFound}</div>
              <div style={{ fontSize: 10, color: '#999' }}>
                {unifiedQueries.length > 0 ? Math.round((totalFound / unifiedQueries.length) * 100) : 0}% of queries
              </div>
            </div>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>Not Shown</div>
              <div className={classes.statValue} style={{ color: '#ef4444' }}>{totalMissing}</div>
              <div style={{ fontSize: 10, color: '#999' }}>opportunities to gain visibility</div>
            </div>
            <div className={classes.statBox}>
              <div className={classes.statLabel}>Avg. Visibility</div>
              <div className={classes.statValue} style={{ color: '#0d9373' }}>{avgVisibility}%</div>
              <div style={{ fontSize: 10, color: '#999' }}>across all queries</div>
            </div>
          </Box>

          {/* Filters */}
          <Paper className={classes.card} style={{ padding: 12 }}>
            <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 8 }}>
              <Typography variant="caption" style={{ fontWeight: 700, color: '#999', textTransform: 'uppercase', marginRight: 4 }}>
                Filters
              </Typography>

              {/* Intent */}
              {[
                { key: 'all', count: unifiedQueries.length },
                { key: 'transactional', count: totalTransactional },
                { key: 'informational', count: totalInformational },
              ].map(({ key: val, count }) => (
                <Chip
                  key={val}
                  label={`${val.charAt(0).toUpperCase() + val.slice(1)} (${count})`}
                  onClick={() => setIntentFilter(val)}
                  className={classes.filterChip}
                  style={{
                    background: intentFilter === val ? '#e8f5e9' : '#f5f5f5',
                    border: intentFilter === val ? '1.5px solid #0d9373' : '1.5px solid #e0e0e0',
                    color: intentFilter === val ? '#0d9373' : '#666',
                  }}
                />
              ))}

              <div style={{ width: 1, height: 20, background: '#e0e0e0' }} />

              {/* Status */}
              <Chip
                label={`Shown (${totalFound})`}
                onClick={() => setStatusFilter(statusFilter === 'found' ? 'all' : 'found')}
                className={classes.filterChip}
                style={{
                  background: statusFilter === 'found' ? '#e8f5e9' : '#f5f5f5',
                  border: statusFilter === 'found' ? '1.5px solid #0d9373' : '1.5px solid #e0e0e0',
                  color: statusFilter === 'found' ? '#0d9373' : '#666',
                }}
              />
              <Chip
                label={`Not shown (${totalMissing})`}
                onClick={() => setStatusFilter(statusFilter === 'missing' ? 'all' : 'missing')}
                className={classes.filterChip}
                style={{
                  background: statusFilter === 'missing' ? '#fef2f2' : '#f5f5f5',
                  border: statusFilter === 'missing' ? '1.5px solid #ef4444' : '1.5px solid #e0e0e0',
                  color: statusFilter === 'missing' ? '#ef4444' : '#666',
                }}
              />

              <div style={{ width: 1, height: 20, background: '#e0e0e0' }} />

              {/* Dropdowns */}
              <FormControl size="small" variant="outlined" style={{ minWidth: 140 }}>
                <Select
                  value={practiceAreaFilter}
                  onChange={(e) => setPracticeAreaFilter(e.target.value)}
                  style={{ fontSize: 12 }}
                >
                  <MenuItem value="all">All Practice Areas</MenuItem>
                  {practiceAreas.map((pa) => (
                    <MenuItem key={pa} value={pa}>{pa}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" variant="outlined" style={{ minWidth: 120 }}>
                <Select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  style={{ fontSize: 12 }}
                >
                  <MenuItem value="all">All Locations</MenuItem>
                  {locations.map((loc) => (
                    <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(intentFilter !== 'all' || statusFilter !== 'all' || practiceAreaFilter !== 'all' || locationFilter !== 'all') && (
                <Chip
                  label="Reset"
                  onClick={() => {
                    setIntentFilter('all');
                    setStatusFilter('all');
                    setPracticeAreaFilter('all');
                    setLocationFilter('all');
                  }}
                  size="small"
                  style={{ background: 'transparent', color: '#999', fontSize: 11 }}
                />
              )}
            </Box>
          </Paper>

          {/* Table */}
          <Paper className={classes.card}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                Queries ({sortedFiltered.length})
              </Typography>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search queries..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 260 }}
                InputProps={{
                  style: { fontSize: 13 },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ fontSize: 18, color: '#999' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {sortedFiltered.length === 0 ? (
              <Typography color="textSecondary" style={{ textAlign: 'center', padding: 24 }}>
                No queries match the current filters.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        { field: 'query', label: 'Query', align: 'left' },
                        { field: 'platform', label: 'Platform', align: 'center' },
                        { field: 'intent', label: 'Intent', align: 'center' },
                        { field: 'status', label: 'Status', align: 'center' },
                        { field: 'rank', label: 'Position', align: 'center' },
                        { field: 'aiVolume', label: 'AI Volume', align: 'right' },
                        { field: 'practiceArea', label: 'Practice Area', align: 'left' },
                        { field: 'city', label: 'Location', align: 'left' },
                      ].map((col) => (
                        <TableCell
                          key={col.field}
                          align={col.align}
                          onClick={() => handleSort(col.field)}
                          style={{ fontWeight: 700, fontSize: 11, color: '#999', cursor: 'pointer', userSelect: 'none' }}
                        >
                          <Box display="inline-flex" alignItems="center" style={{ gap: 4 }}>
                            {col.label}
                            <CustomSortIcon field={col.field} column={sortBy || ''} type={sortDir} />
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedFiltered.map((q) => (
                      <React.Fragment key={q.id}>
                        <TableRow
                          hover
                          onClick={() => setExpandedRow(expandedRow === q.id ? null : q.id)}
                          style={{ cursor: q.snippet ? 'pointer' : 'default' }}
                        >
                          <TableCell style={{ maxWidth: 400 }}>
                            <StyledTooltip title={q.query} placement="top-start" arrow interactive>
                              <Typography variant="body2" style={{ fontSize: 13, lineHeight: 1.4 }} noWrap>
                                {q.query}
                              </Typography>
                            </StyledTooltip>
                            {q.fanOutQueries.length > 0 && (
                              <Typography variant="caption" color="textSecondary" style={{ fontSize: 10 }}>
                                Related: {q.fanOutQueries.slice(0, 2).join(' \u00b7 ')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                padding: '2px 6px',
                                borderRadius: 4,
                                background: q.platform === 'ChatGPT' ? '#10b98118' : '#4285f418',
                                color: q.platform === 'ChatGPT' ? '#059669' : '#4285f4',
                              }}
                            >
                              {q.platform}
                            </span>
                          </TableCell>
                          <TableCell align="center">
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                padding: '2px 6px',
                                borderRadius: 4,
                                background: q.intent === 'transactional' ? '#ede9fe' : '#e0f2fe',
                                color: q.intent === 'transactional' ? '#7c3aed' : '#0284c7',
                              }}
                            >
                              {q.intent === 'transactional' ? 'Transactional' : 'Informational'}
                            </span>
                          </TableCell>
                          <TableCell align="center">
                            <span
                              className={classes.statusPill}
                              style={{
                                background: q.status === 'found' ? '#ecfdf5' : '#fef2f2',
                                color: q.status === 'found' ? '#059669' : '#dc2626',
                                border: q.status === 'found' ? '1px solid #a7f3d0' : '1px solid #fecaca',
                              }}
                            >
                              <span
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  background: q.status === 'found' ? '#10b981' : '#ef4444',
                                }}
                              />
                              {q.status === 'found' ? 'Shown' : 'Not shown'}
                            </span>
                          </TableCell>
                          <TableCell align="center">
                            {q.rank ? (
                              <span
                                className={classes.rankBadge}
                                style={{
                                  background: getRankBg(q.rank),
                                  color: getRankColor(q.rank),
                                }}
                              >
                                #{q.rank}
                              </span>
                            ) : (
                              <span style={{ color: '#ccc', fontSize: 12 }}>&mdash;</span>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12, color: '#0d9373' }}>
                              {q.aiVolume !== null ? q.aiVolume.toLocaleString() : '\u2014'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {q.practiceArea && (
                              <span style={{ fontSize: 11, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                                {q.practiceArea}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {q.city && (
                              <span style={{ fontSize: 11, color: '#666' }}>{q.city}</span>
                            )}
                          </TableCell>
                        </TableRow>
                        {expandedRow === q.id && q.snippet && (
                          <TableRow>
                            <TableCell colSpan={8} style={{ padding: 0, borderBottom: '1px solid #e5e7eb' }}>
                              <Box
                                style={{
                                  background: '#f8f9fa',
                                  padding: '12px 16px',
                                  fontSize: 13,
                                  lineHeight: 1.6,
                                  color: '#333',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  maxHeight: 200,
                                  overflowY: 'auto',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  style={{ fontWeight: 700, color: '#999', display: 'block', marginBottom: 4 }}
                                >
                                  AI Response Snippet
                                </Typography>
                                {q.snippet.split(new RegExp(`(${domain?.split('.')[0]})`, 'gi')).map((part, i) => (
                                  part.toLowerCase() === domain?.split('.')[0]?.toLowerCase()
                                    ? <strong key={`${q.id}-hl-${String(i)}`} style={{ color: '#0d9373' }}>{part}</strong>
                                    : <span key={`${q.id}-txt-${String(i)}`}>{part}</span>
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Footer */}
          <Box display="flex" justifyContent="space-between" mt={2} mb={4}>
            <Typography variant="caption" color="textSecondary">
              Sources: ChatGPT Grid Search + DataForSEO LLM Mentions &middot; {domain}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Cached for 30 days
            </Typography>
          </Box>
        </>
      )}
    </div>
  );
};

export default QueryVisibility;
